import { throwRepoErrorFromApiV3Error } from '@webapp/core/utils/repositories/throwRepoErrorFromApiV3Error'
import { type RepoPaginationResponse } from '@webapp/infrastructure/interfaces/repositories/RepoPaginationResponse'
import { type IHttpClient } from '@webapp/infrastructure/interfaces/services/IHttpClient'
import {
  type IReportExportTaskRepository,
  type ListExportTasksParams,
  type ListExportTasksResult,
} from '@webapp/modules/reports/business-logic/interfaces/IReportExportTaskRepository'
import { type ReportExportTaskApiModel } from '../../api-models/ReportExportTaskApiModel'
import { mapReportExportTaskApiModel } from '../../mappers/mapReportExportTaskApiModel'

interface Dependency {
  httpClient: IHttpClient
}

interface ApiPaginationApiModel {
  count?: number
  page_size?: number
  num_pages?: number
  current_page?: number
}

interface ListExportTasksResponse {
  data: ReportExportTaskApiModel[]
  metadata?: { pagination?: ApiPaginationApiModel }
}

const mapPagination = (
  pagination: ApiPaginationApiModel | undefined
): RepoPaginationResponse => ({
  count: pagination?.count ?? 0,
  pageSize: pagination?.page_size ?? 10,
  numPages: pagination?.num_pages ?? 1,
  page: pagination?.current_page ?? 1,
})

export class ReportExportTaskRepository implements IReportExportTaskRepository {
  constructor(private readonly deps: Dependency) {}

  async list(params: ListExportTasksParams): Promise<ListExportTasksResult> {
    try {
      const res = await this.deps.httpClient.get<ListExportTasksResponse>(
        `/api/v3/export-tasks/`,
        {
          params: {
            format: 'json',
            type: params.types.join(','),
            created_at_0: params.createdFrom,
            created_at_1: params.createdTo,
            order_by: params.orderBy,
            page: params.page,
            page_size: params.pageSize,
          },
        }
      )

      return {
        items: res.data.data.map(mapReportExportTaskApiModel),
        pagination: mapPagination(res.data.metadata?.pagination),
      }
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  // Hard delete (204 No Content). The trailing slash is REQUIRED — the
  // no-slash URL 301-redirects (BHV-037).
  async delete(uuid: string): Promise<void> {
    try {
      await this.deps.httpClient.delete(`/api/v3/export-tasks/${uuid}/`)
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }
}
